// Player stats — stored in localStorage.
// Tracks lifetime aggregate across all days (win rate, streaks, guess distribution).
//
// Schema is versioned so we can migrate later without losing data.

const STORAGE_KEY = 'wdv:stats:v1';

export interface Stats {
  played: number;
  won: number;
  currentStreak: number;
  bestStreak: number;
  lastPlayedDay: number | null; // epoch day of last completed game
  // guessDistribution[i] = number of wins with (i+1) guesses; length = 6
  guessDistribution: number[];
}

export function emptyStats(): Stats {
  return {
    played: 0,
    won: 0,
    currentStreak: 0,
    bestStreak: 0,
    lastPlayedDay: null,
    guessDistribution: [0, 0, 0, 0, 0, 0]
  };
}

export function epochDay(ts: number = Date.now()): number {
  return Math.floor(ts / (24 * 60 * 60 * 1000));
}

export function loadStats(): Stats {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStats();
    const parsed = JSON.parse(raw);
    // Defensive merge in case schema grew
    return { ...emptyStats(), ...parsed };
  } catch {
    return emptyStats();
  }
}

export function saveStats(s: Stats): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // storage disabled / private mode — silently ignore
  }
}

/**
 * Record the result of a completed daily game.
 * @param win true if player solved the puzzle
 * @param attempts 1..6 — attempts used (only counted in distribution on a win)
 * @param day epoch-day of the game; used to track streaks (consecutive days)
 */
export function recordResult(win: boolean, attempts: number, day: number): Stats {
  const s = loadStats();
  // Guard: don't record the same day twice.
  if (s.lastPlayedDay === day) return s;

  s.played += 1;

  if (win) {
    s.won += 1;
    const idx = Math.max(0, Math.min(5, attempts - 1));
    s.guessDistribution[idx] += 1;
    // Extend streak if this is the day right after last, else start at 1.
    if (s.lastPlayedDay !== null && day - s.lastPlayedDay === 1 && s.currentStreak > 0) {
      s.currentStreak += 1;
    } else {
      s.currentStreak = 1;
    }
    if (s.currentStreak > s.bestStreak) s.bestStreak = s.currentStreak;
  } else {
    s.currentStreak = 0;
  }

  s.lastPlayedDay = day;
  saveStats(s);
  return s;
}

/** ms until next epoch-day boundary (UTC midnight). */
export function msUntilNextDay(now: number = Date.now()): number {
  const DAY = 24 * 60 * 60 * 1000;
  return DAY - (now % DAY);
}

/** Format ms as HH:MM:SS countdown. */
export function formatCountdown(ms: number): string {
  if (ms < 0) ms = 0;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}
